import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { formatDate } from "@/lib/utils";
import { Building2, Users, AlertTriangle, Search, Filter, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const LIFECYCLE_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" | "success" | "warning" }> = {
  lead: { label: "Lead", variant: "outline" },
  trial: { label: "Essai", variant: "warning" },
  client: { label: "Client", variant: "success" },
  churned: { label: "Churned", variant: "destructive" },
};

const PRODUCT_LABELS: Record<string, { label: string; className: string }> = {
  travauxbtp: { label: "TravauxBTP", className: "bg-warning/10 text-warning border-warning/20" },
  saas: { label: "SaaS", className: "bg-primary/10 text-primary border-primary/20" },
  mobile: { label: "Mobile", className: "bg-success/10 text-success border-success/20" },
};

export default function Tenants() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [lifecycle, setLifecycle] = useState<string>("");
  const [product, setProduct] = useState<string>("");
  const navigate = useNavigate();

  const { data, isLoading } = trpc.admin.companies.list.useQuery({
    page,
    limit: 20,
    search: search || undefined,
    lifecycle: lifecycle || undefined,
    product: product || undefined,
  } as any);

  const companies = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 20);

  const hasFilters = !!lifecycle || !!product;

  const exportCsv = trpc.admin.companies.export.useMutation({
    onSuccess: (data: any) => {
      const blob = new Blob([data.csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      a.click();
      URL.revokeObjectURL(url);
    },
  });

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Tenants</h1>
            <p className="text-muted-foreground">Vue transversale de toutes les entreprises Constrigo</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportCsv.mutate({} as any)}
            disabled={exportCsv.isPending}
          >
            <Download className="h-4 w-4 mr-2" />
            {exportCsv.isPending ? "Export…" : "Export CSV"}
          </Button>
        </div>

        {/* Stats rapides */}
        <div className="grid gap-3 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total tenants</CardTitle>
              <Building2 className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Page courante</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{companies.length}</div>
              <p className="text-xs text-muted-foreground">résultats affichés</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pages</CardTitle>
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{page} / {totalPages || 1}</div>
            </CardContent>
          </Card>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher une entreprise..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9"
            />
          </div>
          <div className="flex gap-2">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <select
                value={lifecycle}
                onChange={(e) => { setLifecycle(e.target.value); setPage(1); }}
                className="h-10 pl-9 pr-3 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring appearance-none cursor-pointer"
              >
                <option value="">Cycle de vie</option>
                <option value="lead">Lead</option>
                <option value="trial">Essai</option>
                <option value="client">Client</option>
                <option value="churned">Churned</option>
              </select>
            </div>
            <select
              value={product}
              onChange={(e) => { setProduct(e.target.value); setPage(1); }}
              className="h-10 px-3 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring appearance-none cursor-pointer"
            >
              <option value="">Produit</option>
              <option value="travauxbtp">TravauxBTP</option>
              <option value="saas">SaaS</option>
              <option value="mobile">Mobile</option>
            </select>
            {hasFilters && (
              <button
                onClick={() => { setLifecycle(""); setProduct(""); setPage(1); }}
                className="h-10 px-3 rounded-lg border border-input bg-background text-sm text-muted-foreground hover:bg-secondary transition-colors"
              >
                Effacer
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 font-medium text-muted-foreground">Entreprise</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">SIRET</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Statut</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Cycle de vie</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Produits</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Utilisateurs</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Inscrit le</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <tr key={i} className="border-b">
                        <td className="p-4"><Skeleton className="h-4 w-40" /></td>
                        <td className="p-4"><Skeleton className="h-4 w-28" /></td>
                        <td className="p-4"><Skeleton className="h-5 w-16" /></td>
                        <td className="p-4"><Skeleton className="h-5 w-16" /></td>
                        <td className="p-4"><Skeleton className="h-5 w-32" /></td>
                        <td className="p-4"><Skeleton className="h-4 w-8" /></td>
                        <td className="p-4"><Skeleton className="h-4 w-24" /></td>
                      </tr>
                    ))
                  ) : companies.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-muted-foreground">
                        Aucune entreprise trouvée
                      </td>
                    </tr>
                  ) : (
                    companies.map((c: any) => {
                      const lc = LIFECYCLE_CONFIG[c.lifecycle] ?? null;
                      const activeProducts: string[] = c.products
                        ? Object.entries(c.products)
                            .filter(([, v]: [string, any]) => v?.active)
                            .map(([k]) => k)
                        : [];
                      return (
                        <tr
                          key={c.id}
                          className="border-b hover:bg-muted/30 transition-colors cursor-pointer"
                          onClick={() => navigate(`/tenants/${c.id}`)}
                        >
                          <td className="p-4 font-medium">{c.name}</td>
                          <td className="p-4 text-muted-foreground">{c.siret || "—"}</td>
                          <td className="p-4">
                            <Badge variant={c.status === "ACTIVE" ? "success" : "secondary"}>
                              {c.status}
                            </Badge>
                          </td>
                          <td className="p-4">
                            {lc ? (
                              <Badge variant={lc.variant}>{lc.label}</Badge>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="p-4">
                            <div className="flex gap-1 flex-wrap">
                              {activeProducts.length > 0 ? (
                                activeProducts.map((p) => {
                                  const cfg = PRODUCT_LABELS[p];
                                  return cfg ? (
                                    <span
                                      key={p}
                                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cfg.className}`}
                                    >
                                      {cfg.label}
                                    </span>
                                  ) : null;
                                })
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </div>
                          </td>
                          <td className="p-4">{c._count?.User ?? 0}</td>
                          <td className="p-4 text-muted-foreground">{formatDate(c.createdAt)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Page {page} sur {totalPages} — {total} entreprises
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border rounded text-sm disabled:opacity-40 hover:bg-secondary transition-colors"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="px-3 py-1 border rounded text-sm disabled:opacity-40 hover:bg-secondary transition-colors"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
